import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import { isLoggedIn, LogSentryException, Spinner, SpinnerCenter } from "@shapr3d/frontend-lib";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  Navigate,
  redirect,
  Route,
  RouterProvider,
  useLocation,
  useRouteError,
} from "react-router-dom";

import PasswordResetInputContainer from "../../containers/PasswordResetInputContainer";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useLogout } from "../../hooks/useAuth";
import { AuthLayout } from "../../layouts/AuthLayout/AuthLayout";
import { HeaderLayout } from "../../layouts/HeaderLayout/HeaderLayout";
import { LoadGTMLayout } from "../../layouts/LoadGTMLayout/LoadGTMLayout";
import { TeamOwnerLayout } from "../../layouts/TeamsLayout/TeamOwnerLayout";
import { TeamsLayout } from "../../layouts/TeamsLayout/TeamsLayout";
import AccountDeletionPage from "../../pages/AccountDeletionPage";
import AccountDeletionSuccessPage from "../../pages/AccountDeletionSuccessPage";
import AccountPage from "../../pages/AccountPage";
import AppAuthPage from "../../pages/AppAuthPage";
import AuthHandoffPage from "../../pages/AuthHandoffPage";
import ChangeEmailPage from "../../pages/ChangeEmailPage";
import CheckoutPage from "../../pages/CheckoutPage";
import EduRequestConfirmationPage from "../../pages/EduRequestConfirmationPage";
import EduRequestPage from "../../pages/EduRequestPage";
import InstantTrialConfirmationPage from "../../pages/InstantTrialConfirmationPage";
import LogOutZendeskPage from "../../pages/LogOutZendeskPage";
import ModelViewerDebugPage from "../../pages/ModelViewerDebugPage/ModelViewerDebugPage";
import ModelViewerPage from "../../pages/ModelViewerPage/ModelViewerPage";
import NotFoundPage from "../../pages/NotFoundPage/NotFoundPage";
import PasswordResetErrorPage from "../../pages/PasswordResetErrorPage";
import PasswordResetPage from "../../pages/PasswordResetPage";
import PasswordResetSuccessPage from "../../pages/PasswordResetSuccessPage";
import PricingPage from "../../pages/PricingPage";
import ProgressiveProfilingPage from "../../pages/ProgressiveProfilingPage";
import ProjectPage from "../../pages/ProjectPage/ProjectPage";
import SegmentationSurveyPage from "../../pages/SegmentationSurveyPage";
import SharedWithMePage from "../../pages/SharedWithMePage";
import SignInPage from "../../pages/SignInPage";
import SignUpPage from "../../pages/SignUpPage";
import { SSOCallbackPage } from "../../pages/SSOCallbackPage";
import { SSOGeneralErrorPage } from "../../pages/SSOGeneralErrorPage";
import { SSOMismatchErrorPage } from "../../pages/SSOMismatchErrorPage";
import { SSOTimeoutErrorPage } from "../../pages/SSOTimeoutErrorPage";
import TeamBillingPage from "../../pages/TeamBillingPage";
import TeamInvitationSignUpSelfServePage from "../../pages/TeamInvitationSignUpSelfServePage";
import TeamMembersPage from "../../pages/TeamMembersPage";
import { TeamPaymentSuccessPage } from "../../pages/TeamPaymentSuccessPage";
import TeamSettingsPage from "../../pages/TeamSettingsPage";
import TeamSubscriptionPage from "../../pages/TeamSubscriptionPage";
import UpdateNamePage from "../../pages/UpdateNamePage";
import VerifyEduEmailPage from "../../pages/VerifyEduEmailPage";
import VerifyEmailErrorPage from "../../pages/VerifyEmailErrorPage";
import VerifyEmailSuccessPage from "../../pages/VerifyEmailSuccessPage";
import LinkListPage from "../../pages/ViewLinkListPage/ViewLinkListPage";
import VoucherClaimPage from "../../pages/VoucherClaimPage";
import ZendeskAuthPage from "../../pages/ZendeskAuthPage";
import ErrorPage from "../ErrorPage/ErrorPage";
import NavigateToAccountWhenLoggedIn from "../NavigateToAccountWhenLoggedIn/NavigateToAccountWhenLoggedIn";
import TeamInvitationError from "../TeamInvitationError/TeamInvitationError";
import { AppProviders } from "./components/AppProviders";

const LegacySubscriptionRedirect = () => {
  const navigation = useNavigation();
  const { data: accountData } = useAccount();

  useEffect(() => {
    if (accountData) {
      const teamIDToNavigateTo =
        accountData.teams.find(({ isPrimary }) => isPrimary)?.id ?? accountData.teams[0].id;
      navigation.goToTeamSubscription(teamIDToNavigateTo);
    }
  }, [accountData, navigation]);

  if (!isLoggedIn()) {
    return <DefaultRedirect />;
  }

  return <SpinnerCenter />;
};

const MembersRedirect = () => {
  const navigation = useNavigation();
  const { data: accountData } = useAccount();
  const { search } = useLocation();

  useEffect(() => {
    if (accountData) {
      const searchParams = new URLSearchParams(search);
      const teamIDToNavigateTo =
        accountData.teams.find(({ isPrimary }) => isPrimary)?.id ?? accountData.teams[0].id;
      navigation.goToTeamMembers({
        teamID: teamIDToNavigateTo,
        additionalQueryParams: Object.fromEntries(searchParams),
      });
    }
  }, [accountData, navigation, search]);

  if (!isLoggedIn()) {
    return <DefaultRedirect />;
  }

  return <SpinnerCenter />;
};

export const DefaultRedirect = () => {
  const location = useLocation();

  return <Navigate to={{ pathname: "/teams", search: location.search }} replace={true} />;
};

const RoutingErrorFallback = () => {
  const error = useRouteError();

  useEffect(() => {
    LogSentryException({ messagePrefix: "Routing error", error, level: "fatal" });
  }, [error]);

  return <ErrorPage />;
};

const Logout = () => {
  const { mutateAsync } = useLogout({ withRequest: true, redirect: false });
  useEffect(() => {
    (async () => {
      if (isLoggedIn()) {
        await mutateAsync();
      }
    })();
  }, [mutateAsync]);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Spinner />
      Logging out
    </div>
  );
};

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const appRoutes = () => (
  <Route element={<AppProviders />} errorElement={<RoutingErrorFallback />}>
    {/* GTM Loaded */}
    <Route element={<LoadGTMLayout />}>
      {/* Routes without header */}
      <Route path="pricing" element={<PricingPage />} />

      <Route path="login/app" element={<AuthHandoffPage />} />
      {/* Routes with Header layout */}
      <Route element={<HeaderLayout />}>
        <Route path="logout" element={<Logout />} />
        <Route
          path="login"
          element={
            <NavigateToAccountWhenLoggedIn>
              <SignInPage />
            </NavigateToAccountWhenLoggedIn>
          }
        />
        {/* // TODO: Why is this inside the headered routes */}
        <Route path="auth/app" element={<AppAuthPage />} />
        <Route
          path="signup"
          element={
            <NavigateToAccountWhenLoggedIn>
              <SignUpPage />
            </NavigateToAccountWhenLoggedIn>
          }
        />
        <Route path="email-verify">
          <Route path="success" element={<VerifyEmailSuccessPage />} />
          <Route path="error" element={<VerifyEmailErrorPage />} />
        </Route>
        <Route path="password-reset">
          <Route path="" element={<PasswordResetPage />} />
          <Route path="error" element={<PasswordResetErrorPage />} />
          <Route path="success" element={<PasswordResetSuccessPage />} />
        </Route>
        <Route path="auth/zendesk">
          <Route path="login" element={<ZendeskAuthPage />} />
          <Route path="logout" element={<LogOutZendeskPage />} />
        </Route>
        <Route path="account-deletion-success" element={<AccountDeletionSuccessPage />} />
        {/* //! This is somehow not authenticated, because it redirects to signUp, but otherwise requires authentication */}
        <Route path="claim" element={<VoucherClaimPage />} />
        {/* //! This route is fishy, why is it only success?  */}
        <Route path="instant-trial-confirmation" element={<InstantTrialConfirmationPage />} />
      </Route>

      {/* Authenticated routes */}
      <Route element={<AuthLayout />}>
        <Route path="progressive_profiling_survey" element={<ProgressiveProfilingPage />} />
        <Route path="auth/wv" element={<DefaultRedirect />} />

        {/* Routes with Header layout */}
        <Route element={<HeaderLayout />}>
          <Route path="segmentation-survey" element={<SegmentationSurveyPage />} />
          <Route path="update-name" element={<UpdateNamePage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="payment-success" element={<TeamPaymentSuccessPage />} />
          <Route path="edu">
            <Route path="" element={<EduRequestPage />} />
            <Route path="confirmation" element={<EduRequestConfirmationPage />} />
          </Route>
          <Route path="account-deletion" element={<AccountDeletionPage />} />
        </Route>

        {/* Routes with Teams layout */}
        <Route path="teams" element={<TeamsLayout />}>
          <Route path=":teamID">
            <Route index element={<AccountPage />} />
            <Route path="members" element={<TeamMembersPage />} />
            <Route element={<TeamOwnerLayout />}>
              <Route path="subscription" element={<TeamSubscriptionPage />} />
              <Route path="billing" element={<TeamBillingPage />} />
            </Route>
            <Route path="settings" element={<TeamSettingsPage />} />
          </Route>
        </Route>
      </Route>
    </Route>

    {/* Sensitive routes */}
    <Route path={"sso"}>
      <Route path="callback" element={<SSOCallbackPage />} />
      <Route element={<HeaderLayout />}>
        <Route path="error" element={<SSOGeneralErrorPage />} />
        <Route path="timeout" element={<SSOTimeoutErrorPage />} />
        <Route path="mismatch" element={<SSOMismatchErrorPage />} />
      </Route>
    </Route>
    <Route path="edu/verify/:hash" element={<VerifyEduEmailPage />} />
    <Route element={<HeaderLayout />}>
      <Route path="password-reset/reset" element={<PasswordResetInputContainer />} />
      <Route path="password-reset/:confirmationHash" element={<PasswordResetInputContainer />} />
      <Route
        path="team/register/:invitationHash"
        loader={({ params }) => {
          const { invitationHash } = params;
          return redirect(`/teams/invitation/${invitationHash}`);
        }}
      />
      <Route path="teams/invitation">
        <Route path=":invitationHash" element={<TeamInvitationSignUpSelfServePage />} />
        <Route path="" element={<TeamInvitationError errorType="Invalid" />} />
      </Route>
      <Route path="change-email" element={<ChangeEmailPage />} />
    </Route>

    <Route path="/v/:viewLink">
      <Route index element={<ModelViewerPage />} />
      <Route path="drawings/:drawingId" element={<ModelViewerPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
    <Route path="/p/:projectId">
      <Route index element={<ProjectPage />} />
      <Route path="*" element={<NotFoundPage isProjectPage />} />
    </Route>
    <Route path="/auth" element={<DefaultRedirect />} />
    <Route element={<AuthLayout />}>
      <Route path="teams" element={<TeamsLayout />}>
        <Route path=":teamID">
          <Route path="review-links?" element={<LinkListPage />} />
          <Route path="shared-with-me" element={<SharedWithMePage />} />
        </Route>
      </Route>
    </Route>
    <Route path="/debug" element={<ModelViewerDebugPage />} />

    <Route element={<AuthLayout />}>
      {/** NOTE: To redirect legacy app links to the new teams layout */}
      <Route path="/subscription" element={<LegacySubscriptionRedirect />} />
      {/** NOTE: To be able to use the url: /members?intent=invite without knowing anything about the user */}
      <Route path="/members" element={<MembersRedirect />} />
    </Route>
    <Route path="/" element={<DefaultRedirect />} />
    <Route path="*" element={<DefaultRedirect />} />
  </Route>
);

const router = sentryCreateBrowserRouter(createRoutesFromChildren(appRoutes()));

export const App: React.FC = () => {
  return <RouterProvider router={router}></RouterProvider>;
};
